import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "./client";
import { api, withAuth } from "./session";
import type {
  ClaimAuthorization,
  ClaimRecord,
  ClaimResponse,
  EpochResponse,
  LeaderboardResponse,
  Me,
  ReferralsResponse,
  WalletConnectResponse,
} from "./types";

export const keys = {
  me: ["me"] as const,
  claims: ["me", "claims"] as const,
  epoch: ["epoch"] as const,
  referrals: ["referrals"] as const,
  leaderboard: ["leaderboard"] as const,
  airdrop: ["airdrop"] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => withAuth(() => api.get<Me>("/me")),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useMyClaims() {
  return useQuery({
    queryKey: keys.claims,
    queryFn: () => withAuth(() => api.get<{ claims: ClaimRecord[] }>("/me/claims")),
    staleTime: 30_000,
  });
}

export function useEpoch() {
  return useQuery({
    queryKey: keys.epoch,
    queryFn: () => api.get<EpochResponse>("/epoch"),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useReferrals() {
  return useQuery({
    queryKey: keys.referrals,
    queryFn: () => withAuth(() => api.get<ReferralsResponse>("/referrals")),
    staleTime: 30_000,
  });
}

export function useLeaderboard() {
  return useQuery({
    queryKey: keys.leaderboard,
    queryFn: () => api.get<LeaderboardResponse>("/leaderboard"),
    staleTime: 60_000,
  });
}

export function useClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => withAuth(() => api.post<ClaimResponse>("/claim")),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.claims });
      void qc.invalidateQueries({ queryKey: keys.epoch });
      void qc.invalidateQueries({ queryKey: keys.leaderboard });
    },
    onError: () => {
      // ALREADY_CLAIMED etc. — server state is authoritative; refresh what we show.
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Claim-reminder preference; optimistic so the switch flips instantly. */
export function useSetReminders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      withAuth(() =>
        api.post<{ settings: { remindersEnabled: boolean } }>("/me/settings", {
          remindersEnabled: enabled,
        }),
      ),
    onMutate: (enabled) => {
      qc.setQueryData<Me>(keys.me, (old) =>
        old ? { ...old, settings: { ...old.settings, remindersEnabled: enabled } } : old,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export interface TonProofSubmission {
  address: string;
  publicKey: string;
  stateInit: string;
  proof: {
    timestamp: number;
    domain: { lengthBytes: number; value: string };
    signature: string;
    payload: string;
  };
}

export function useWalletConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TonProofSubmission) =>
      withAuth(() => api.post<WalletConnectResponse>("/wallet/connect", body)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/**
 * Airdrop claim state. Resolves to a status object instead of throwing for the expected
 * "not yet" / "not you" answers so the Wallet tab can render them as copy, not errors.
 */
export type AirdropState =
  | { status: "not_open" }
  | { status: "no_allocation" }
  | { status: "excluded" }
  | { status: "not_signed" }
  | { status: "ready"; auth: ClaimAuthorization };

export function useAirdrop() {
  return useQuery<AirdropState>({
    queryKey: keys.airdrop,
    queryFn: async () => {
      try {
        const auth = await withAuth(() => api.get<ClaimAuthorization>("/claim/authorization"));
        return { status: "ready", auth };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.code === "CLAIM_NOT_CONFIGURED") return { status: "not_open" };
          if (err.code === "NO_ALLOCATION") return { status: "no_allocation" };
          if (err.code === "EXCLUDED") return { status: "excluded" };
          if (err.code === "NOT_SIGNED") return { status: "not_signed" };
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: 1,
  });
}

export function fetchProofPayload(): Promise<{ payload: string; expiresIn: number }> {
  return withAuth(() => api.get<{ payload: string; expiresIn: number }>("/wallet/proof-payload"));
}
